import { useEffect, useState } from "react";
import useSWR from "swr";
import { ExternalLink, RefreshCw, ToggleLeft, ToggleRight } from "lucide-react";
import {
  ApiError,
  apiFetch,
  swrFetcher,
  type AdminPricingStatus,
  type AdminProvider,
  type PricingPolicyInfo,
  type PricingSyncRunInfo,
} from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { formatTimestamp } from "@/lib/format";

export function ProvidersTab() {
  const { data: providers, isLoading, mutate } = useSWR<AdminProvider[]>("/api/admin/providers", swrFetcher);
  const { data: pricing, mutate: mutatePricing } = useSWR<AdminPricingStatus[]>("/api/admin/pricing", swrFetcher);
  const { data: policies, mutate: mutatePolicies } = useSWR<PricingPolicyInfo[]>("/api/admin/pricing/policies", swrFetcher);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [markupPercent, setMarkupPercent] = useState("0");
  const [fixedFeeUsd, setFixedFeeUsd] = useState("0");
  const [savingPolicy, setSavingPolicy] = useState(false);

  useEffect(() => {
    const global = policies?.find((policy) => policy.scope_type === "global" && policy.active);
    if (!global) return;
    setMarkupPercent(global.markup_percent);
    setFixedFeeUsd(global.fixed_fee_usd);
  }, [policies]);

  const saveGlobalPolicy = async () => {
    setSavingPolicy(true);
    setSyncError(null);
    try {
      await apiFetch("/api/admin/pricing/policies", {
        method: "POST",
        body: JSON.stringify({
          scope_type: "global",
          markup_percent: Number(markupPercent),
          fixed_fee_usd: Number(fixedFeeUsd),
        }),
      });
      await mutatePolicies();
    } catch (error) {
      setSyncError(error instanceof ApiError ? error.message : "Ücret politikası kaydedilemedi");
    } finally {
      setSavingPolicy(false);
    }
  };

  const toggleActive = async (p: AdminProvider) => {
    try {
      await apiFetch(`/api/admin/providers/${p.id}`, { method: "PATCH", body: JSON.stringify({ active: !p.active }) });
      mutate();
    } catch (cause) {
      // Previously an unhandled rejection: the toggle silently did nothing.
      console.error(cause);
      window.alert(cause instanceof Error ? cause.message : "Update failed");
    }
  };

  const syncPricing = async (providerKey: string) => {
    setSyncing(providerKey);
    setSyncError(null);
    try {
      await apiFetch<PricingSyncRunInfo>(`/api/admin/pricing/${providerKey}/sync`, {
        method: "POST",
      });
      await mutatePricing();
    } catch (error) {
      setSyncError(error instanceof ApiError ? error.message : "Fiyatlar güncellenemedi");
    } finally {
      setSyncing(null);
    }
  };

  if (isLoading || !providers) return <p className="text-sm text-studio-muted">Loading providers…</p>;

  const pricingByProvider = new Map((pricing ?? []).map((item) => [item.provider_key, item]));

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight sm:text-2xl">Providers</h1>
          <p className="mt-1 text-xs text-studio-faint">
            Makine-okunur kaynaklar otomatik, doküman kaynakları son geçerlilik tarihine kadar doğrulanmış fiyatla çalışır.
          </p>
        </div>
      </div>
      {syncError && (
        <div className="mb-4 rounded-lg border border-studio-danger/30 bg-studio-danger/10 px-3 py-2 text-sm text-studio-danger">
          {syncError} Mevcut doğrulanmış fiyatlar değiştirilmedi.
        </div>
      )}
      <Card className="mb-4 p-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="font-display text-sm font-semibold">Global user charge policy</h2>
            <p className="mt-1 text-xs text-studio-faint">User charge = provider cost + percentage markup + fixed USD fee. Provider and model overrides are also supported by the API.</p>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-end">
            <label className="text-xs text-studio-muted">Markup %<Input className="mt-1 w-full sm:w-28" type="number" min="0" step="0.01" value={markupPercent} onChange={(event) => setMarkupPercent(event.target.value)} /></label>
            <label className="text-xs text-studio-muted">Fixed USD<Input className="mt-1 w-full sm:w-28" type="number" min="0" step="0.0001" value={fixedFeeUsd} onChange={(event) => setFixedFeeUsd(event.target.value)} /></label>
            <Button className="col-span-2" size="sm" loading={savingPolicy} onClick={() => void saveGlobalPolicy()}>Save policy</Button>
          </div>
        </div>
      </Card>
      <div className="grid gap-4 lg:grid-cols-2">
        {providers.map((p) => {
          const priceStatus = pricingByProvider.get(p.key);
          const latest = priceStatus?.latest_run;
          const latestVerifiedAt = priceStatus?.rates
            .map((rate) => rate.verified_at)
            .filter((value): value is string => Boolean(value))
            .sort()
            .at(-1);
          const earliestExpiry = priceStatus?.rates
            .map((rate) => rate.valid_until)
            .filter((value): value is string => Boolean(value))
            .sort()
            .at(0);
          return (
          <Card key={p.id} className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-display font-semibold">{p.name}</h3>
                  <Badge variant={p.active ? "success" : "danger"}>{p.active ? "Active" : "Disabled"}</Badge>
                </div>
                <p className="mt-1 text-xs text-studio-faint">
                  key: <code className="font-mono text-studio-accent">{p.key}</code> · {p.type} · {p.model_count} model(s)
                </p>
                <p className="mt-1 break-all text-xs text-studio-faint">Auth: {p.auth_method} · {p.base_url}</p>
                <p className="mt-1 text-xs text-studio-faint">
                  Secret: <code className="font-mono">{p.secret_ref}</code>
                </p>
              </div>
              <button
                onClick={() => toggleActive(p)}
                className={cn(
                  "flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition-colors",
                  p.active ? "text-studio-success hover:bg-studio-success/10" : "text-studio-danger hover:bg-studio-danger/10"
                )}
                title={p.active ? "Disable" : "Enable"}
              >
                {p.active ? <ToggleRight className="h-6 w-6" /> : <ToggleLeft className="h-6 w-6" />}
              </button>
            </div>
            {priceStatus && (
              <div className="mt-4 border-t border-studio-border pt-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="text-xs text-studio-faint">
                    <p>
                      {priceStatus.rates.length} aktif fiyat · Son doğrulama: {latestVerifiedAt
                        ? formatTimestamp(latestVerifiedAt, { locale: "tr-TR" })
                        : "Henüz yok"}
                    </p>
                    {earliestExpiry && <p className="mt-1">En yakın geçerlilik sonu: {formatTimestamp(earliestExpiry, { locale: "tr-TR" })}</p>}
                    {latest && (
                      <p className={cn("mt-1", latest.status === "failed" && "text-studio-danger")}>
                        Son kontrol: {latest.status === "succeeded" ? "başarılı" : latest.status === "failed" ? "başarısız" : "çalışıyor"}
                        {latest.status === "succeeded" && ` · ${latest.updated_count} değişti, ${latest.unchanged_count} aynı`}
                      </p>
                    )}
                  </div>
                  <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
                    {priceStatus.source_url && <a
                      href={priceStatus.source_url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex h-11 w-11 items-center justify-center rounded-lg text-studio-muted transition-colors hover:bg-studio-card hover:text-studio-ink"
                      title="Resmî fiyat kaynağı"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>}
                    {priceStatus.source_supported && <Button
                      className="min-w-0 flex-1 sm:flex-none"
                      size="sm"
                      variant="secondary"
                      loading={syncing === p.key}
                      onClick={() => syncPricing(p.key)}
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      Fiyatları güncelle
                    </Button>}
                  </div>
                </div>
              </div>
            )}
          </Card>
          );
        })}
      </div>
    </div>
  );
}
